<template>
    <div v-if="meta && meta.last_page" class="pagination-bar">
        <div class="pagination-bar__pages">
            <button
                v-if="currentPage > 1"
                type="button"
                class="pagination-bar__link focus:outline-none"
                @click="goToPage(1)"
            >
                в начало
            </button>
            <button
                v-if="currentPage > 1"
                type="button"
                class="pagination-bar__link focus:outline-none"
                @click="goToPage(currentPage - 1)"
            >
                назад
            </button>

            <template v-for="item in pageItems" :key="item.key">
                <span v-if="item.type === 'gap'" class="pagination-bar__gap"
                    >…</span
                >
                <button
                    v-else
                    type="button"
                    class="pagination-bar__page focus:outline-none"
                    :class="{
                        'pagination-bar__page--active':
                            item.page === currentPage,
                    }"
                    @click="goToPage(item.page)"
                >
                    {{ item.page }}
                </button>
            </template>

            <button
                v-if="currentPage < lastPage"
                type="button"
                class="pagination-bar__link focus:outline-none"
                @click="goToPage(currentPage + 1)"
            >
                вперёд
            </button>
            <button
                v-if="currentPage < lastPage"
                type="button"
                class="pagination-bar__link focus:outline-none"
                @click="goToPage(lastPage)"
            >
                в конец
            </button>
        </div>

        <div class="pagination-bar__per-page">
            <PerPageDropdown
                :value="perPage"
                :options="perPages"
                @select="$emit('set-per-page', $event)"
            />
        </div>
    </div>
</template>

<script>
import PerPageDropdown from "./PerPageDropdown.vue";

export default {
    name: "PaginationBar",
    components: { PerPageDropdown },
    props: {
        meta: {
            type: Object,
            default: null,
        },
        page: {
            type: [Number, String],
            required: true,
        },
        perPage: {
            type: Number,
            required: true,
        },
        perPages: {
            type: Array,
            required: true,
        },
    },
    emits: ["set-page", "set-per-page"],
    computed: {
        lastPage() {
            return Number(this.meta?.last_page ?? 1);
        },
        currentPage() {
            const parsed = Number(this.page ?? 1);
            return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
        },
        pageItems() {
            const last = this.lastPage;
            const current = this.currentPage;

            if (!Number.isFinite(last) || last <= 1) {
                return [{ type: "page", page: 1, key: "page-1" }];
            }

            if (last <= 7) {
                return Array.from({ length: last }, (_, idx) => {
                    const page = idx + 1;
                    return { type: "page", page, key: `page-${page}` };
                });
            }

            const pages = new Set([
                1,
                last,
                current - 1,
                current,
                current + 1,
                2,
                last - 1,
            ]);
            const sorted = Array.from(pages)
                .filter((p) => p >= 1 && p <= last)
                .sort((a, b) => a - b);

            const items = [];
            let prev = null;
            for (const p of sorted) {
                if (prev !== null && p - prev > 1) {
                    items.push({ type: "gap", key: `gap-${prev}-${p}` });
                }
                items.push({ type: "page", page: p, key: `page-${p}` });
                prev = p;
            }
            return items;
        },
    },
    methods: {
        goToPage(target) {
            const next = Number(target);
            if (!Number.isFinite(next)) return;
            if (next < 1 || next > this.lastPage) return;
            if (next === this.currentPage) return;
            this.$emit("set-page", next);
        },
    },
};
</script>

<style scoped>
.pagination-bar {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
}
.pagination-bar__pages {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    background-color: #f8fafc;
    border-radius: 2px;
}
.pagination-bar__page {
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid transparent;
    border-radius: 2px;
    font-size: 14px;
    line-height: 1;
    color: #111827;
}
.pagination-bar__page:hover {
    border-color: #25a24c;
}
.pagination-bar__page--active {
    font-weight: 700;
    border: 1px solid #00a652;
    border-radius: 2px;
}
.pagination-bar__gap {
    font-size: 14px;
    color: #6b7280;
    user-select: none;
}
.pagination-bar__link {
    height: 28px;
    padding: 0 4px;
    border: none;
    background: transparent;
    font-size: 14px;
    line-height: 1;
    color: #6b7280;
    text-decoration: none;
}
.pagination-bar__link:hover {
    color: #111827;
    text-decoration: underline;
}
</style>
